/* Keys, key signatures and Roman-numeral degrees. */
import type { ChordQuality } from '../dsp/chord';
import { letter, mod, pcName, pretty } from './notes';

export type Mode = 'major' | 'minor';

const FLAT_MAJOR = new Set([5, 10, 3, 8, 1]);
const FLAT_MINOR = new Set([2, 7, 0, 5, 10]);

/** Whether a key is conventionally spelled with flats. */
export const keyFlat = (tonic: number, mode: Mode): boolean =>
  (mode === 'minor' ? FLAT_MINOR : FLAT_MAJOR).has(mod(tonic, 12));

/** "Sol (G) majör" in the user's naming system. */
export function keyName(tonic: number, mode: Mode): string {
  const flat = keyFlat(tonic, mode);
  return `${pcName(tonic, { flat })} ${mode === 'minor' ? 'minör' : 'majör'}`;
}

export const keySymbol = (tonic: number, mode: Mode): string =>
  pretty(letter(tonic, keyFlat(tonic, mode)) + (mode === 'minor' ? 'm' : ''));

interface Degree {
  /** semitones above the tonic */
  iv: number;
  q: ChordQuality;
}

export const DEGREES: Record<Mode, Record<string, Degree>> = {
  major: {
    I: { iv: 0, q: '' },
    ii: { iv: 2, q: 'm' },
    iii: { iv: 4, q: 'm' },
    IV: { iv: 5, q: '' },
    V: { iv: 7, q: '' },
    vi: { iv: 9, q: 'm' },
    'vii°': { iv: 11, q: 'dim' },
    V7: { iv: 7, q: '7' },
    Imaj7: { iv: 0, q: 'maj7' },
    ii7: { iv: 2, q: 'm7' },
    IVmaj7: { iv: 5, q: 'maj7' },
    vi7: { iv: 9, q: 'm7' },
    iii7: { iv: 4, q: 'm7' },
    iv: { iv: 5, q: 'm' },
    '♭VII': { iv: 10, q: '' },
    '♭VI': { iv: 8, q: '' },
    '♭III': { iv: 3, q: '' },
    'V/vi': { iv: 4, q: '7' },
    'V/V': { iv: 2, q: '7' },
    'V/IV': { iv: 0, q: '7' },
    'V/ii': { iv: 9, q: '7' },
    Isus4: { iv: 0, q: 'sus4' },
    Vsus4: { iv: 7, q: 'sus4' },
    I7: { iv: 0, q: '7' },
    IV7: { iv: 5, q: '7' },
  },
  minor: {
    i: { iv: 0, q: 'm' },
    'ii°': { iv: 2, q: 'dim' },
    III: { iv: 3, q: '' },
    iv: { iv: 5, q: 'm' },
    v: { iv: 7, q: 'm' },
    V: { iv: 7, q: '' },
    V7: { iv: 7, q: '7' },
    VI: { iv: 8, q: '' },
    VII: { iv: 10, q: '' },
    i7: { iv: 0, q: 'm7' },
    iv7: { iv: 5, q: 'm7' },
    IV: { iv: 5, q: '' },
    II: { iv: 2, q: '' },
  },
};

export interface KeyChord {
  root: number;
  q: ChordQuality;
  deg: string;
  sym: string;
}

export function chordInKey(deg: string, tonic: number, mode: Mode): KeyChord {
  const d = DEGREES[mode][deg];
  if (!d) throw new Error(`Bilinmeyen derece: ${deg}`);
  const root = mod(tonic + d.iv, 12);
  const flat = keyFlat(tonic, mode);
  return { root, q: d.q, deg, sym: letter(root, flat) + d.q };
}

export const DIATONIC: Record<Mode, readonly string[]> = {
  major: ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'],
  minor: ['i', 'ii°', 'III', 'iv', 'v', 'VI', 'VII'],
};
